import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { useDropzone } from 'react-dropzone';
import { PublicationForm } from '../types';
import thumbnail from '@web/vin-sessions/assets/Thumbnail.png';
import { SelectPopover } from '@web/administration/views/worksheets/components/selectPopover';
import { PublicationModal } from './stis-publications/publication-modal';
import { UploadPhase } from './stis-publications/hooks/use-upload-status';
import { DOCUMENT_ACCEPT_TYPES, ZIP_ACCEPT_TYPES } from './stis-publications/content-file.utils';
import { TransferProgress, formatTransferProgress } from '@web/vin-sessions/utils/upload-transfer';
import colors from '@packages/core/styles/colors';

const useStyles = createUseStyles({
    uploadBox: {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        minHeight: 144,
        '&:hover': {
            backgroundColor: '#e9ecef',
        },
        '& .text-center': {
            color: '#6c757d',
        },
        '&.disabled': {
            backgroundColor: '#f5f5f5',
            cursor: 'not-allowed',
            opacity: 0.6,
        },
    },
    image: {
        height: '100%',
        width: '100%',
        marginBottom: 8,
    },
    skeletonLoader: {
        background: 'linear-gradient(90deg, #e3e3e3 25%, #f0f0f0 37%, #e3e3e3 63%)',
        animation: '$skeletonLoading 2s ease-in-out infinite',
        backgroundSize: '400px 100%',
    },
    skeletonThumbnail: {
        width: '100%',
        height: 64,
        borderRadius: 8,
    },
    skeletonFilename: {
        width: 280,
        height: 20,
        borderRadius: 4,
    },
    skeletonDateAuthor: {
        width: 180,
        height: 16,
        borderRadius: 4,
    },
    skeletonMenu: {
        width: 30,
        height: 30,
        borderRadius: '50%',
    },
    '@keyframes skeletonLoading': {
        '0%': {
            backgroundPosition: '-200px 0',
        },
        '100%': {
            backgroundPosition: 'calc(200px + 100%) 0',
        },
    },
    progressTrack: {
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.grayThree,
        overflow: 'hidden',
        marginTop: 4,
    },
    progressFill: {
        height: '100%',
        borderRadius: 3,
        backgroundColor: colors.blueOne,
        transition: 'width 0.3s ease',
    },
    statusCard: {
        border: '1px solid #999DA3',
        padding: 16,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
    },
    fileUploadRow: {
        border: '1px solid #999DA3',
        padding: 16,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        tabIndex: -1,
    },
    fileUploadThumbnail: {
        marginRight: 16,
        width: 64,
        height: 64,
    },
    fileUploadContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    fileUploadDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
});

interface Props {
    label: string;
    iconName: string;
    value?: File | null;
    placeholder1: string;
    placeholder2?: string;
    onChange: (file: File | null) => void;
    disabled?: boolean;
    publicationInfo: PublicationForm;
    deleteAction: () => void;
    errorMessage: string;
    fileUploadStatus: boolean;
    sendProgress?: TransferProgress;
    isServiceDiagnostics?: boolean;
    acceptedFileTypes?: string[];
    uploadPhase?: UploadPhase;
    lastCheckedAt?: Date;
    onCheckAgain?: () => void;
    fileAction?: { label: string; onOpen: () => void };
}

const UPLOAD_STATUS_MESSAGES: Partial<Record<UploadPhase, { title: string; detail?: string }>> = {
    slow: {
        title: 'Processing this file is taking longer than expected.',
        detail: 'Please check back later.',
    },
    stillProcessing: {
        title: 'This file is still processing.',
        detail: 'Please check back later.',
    },
    checkFailed: {
        title: "Couldn't check the file status.",
    },
};

export const FileUpload = ({
    label,
    iconName,
    value,
    onChange,
    placeholder1,
    placeholder2,
    disabled = false,
    publicationInfo,
    deleteAction,
    errorMessage,
    fileUploadStatus,
    sendProgress,
    isServiceDiagnostics = false,
    acceptedFileTypes,
    uploadPhase = 'idle',
    lastCheckedAt,
    onCheckAgain,
    fileAction,
}: Props) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [showDeletePublication, setShowDeletePublication] = useState<boolean>(false);
    const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
    const [fileTypeError, setFileTypeError] = useState<string>('');

    const actions = {
        delete: t('Remove'),
    };

    const actionMenu = useMemo(() => {
        const menu: { label: string; value: string; action: () => void }[] = [];
        const deleteAction = {
            label: actions.delete,
            value: 'remove',
            action: () => setShowDeletePublication(true),
        };

        if (fileAction) {
            menu.push({
                label: fileAction.label,
                value: 'open',
                action: () => {
                    setShowStatusModal(false);
                    fileAction.onOpen();
                },
            });
        }
        menu.push(deleteAction);
        return menu;
    }, [actions.delete, fileAction]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (!disabled && acceptedFiles.length > 0) {
                setFileTypeError('');
                onChange(acceptedFiles[0]);
            }
        },
        [onChange, disabled]
    );

    const allowedTypes = useMemo(
        () =>
            acceptedFileTypes ??
            (isServiceDiagnostics ? [...DOCUMENT_ACCEPT_TYPES, ...ZIP_ACCEPT_TYPES] : DOCUMENT_ACCEPT_TYPES),
        [acceptedFileTypes, isServiceDiagnostics]
    );

    const onDropRejected = useCallback(() => {
        const allowedLabel = allowedTypes.some((type) => type.includes('zip'))
            ? t(
                  'stisPublications:documentInfo.errors.unsupportedFileTypeWithZip',
                  'Unsupported file type. Please upload a PDF, DOC, DOCX, or ZIP file.'
              )
            : t(
                  'stisPublications:documentInfo.errors.unsupportedFileType',
                  'Unsupported file type. Please upload a PDF, DOC, or DOCX file.'
              );
        setFileTypeError(allowedLabel);
    }, [allowedTypes, t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: allowedTypes,
        multiple: false,
        disabled,
    });

    return (
        <>
            <PublicationModal
                show={showDeletePublication}
                title={t('Delete File?')}
                bodyHeader={t('Are you sure you want to remove this file?')}
                bodyContent={t('It will be removed permanently and cannot be undone.')}
                cancelButton={t('stisPublications:buttons.cancel', 'Cancel')}
                confirmButton={t('Remove')}
                confirmButtonLoading={false}
                handleCancelAction={() => setShowDeletePublication(false)}
                handleConfirmAction={() => {
                    deleteAction();
                    setShowDeletePublication(false);
                    setShowStatusModal(false);
                }}
                onHideModal={() => setShowDeletePublication(false)}
                variant="danger"
            />
            <div className="mb-2">
                <Label required>{label}</Label>
            </div>
            {fileUploadStatus === true && sendProgress ? (
                <div className={classes.statusCard} role="status" aria-live="polite">
                    <Icon name="export" color="blueOne" size={24} />
                    <div className={classes.fileUploadDetails} style={{ flex: 1 }}>
                        <Typography style={{ fontWeight: 600, fontSize: 15 }}>
                            {t('stisPublications:documentInfo.upload.progressTitle', 'Uploading {{percent}}%', {
                                percent: Math.floor((sendProgress.loaded / sendProgress.total) * 100),
                            })}
                        </Typography>
                        <Typography style={{ fontSize: 13, color: '#4C525D' }}>
                            {t(
                                'stisPublications:documentInfo.upload.progressDetail',
                                '{{progress}}. Keep this page open until the upload finishes, or the file is lost.',
                                { progress: formatTransferProgress(sendProgress) }
                            )}
                        </Typography>
                        <div
                            className={classes.progressTrack}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.floor((sendProgress.loaded / sendProgress.total) * 100)}
                        >
                            <div
                                className={classes.progressFill}
                                style={{ width: `${(sendProgress.loaded / sendProgress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>
            ) : fileUploadStatus === true || uploadPhase === 'processing' ? (
                <div className={classes.fileUploadRow} aria-disabled={disabled}>
                    <div className={classes.fileUploadThumbnail}>
                        <div className={`${classes.skeletonLoader} ${classes.skeletonThumbnail}`} />
                    </div>
                    <div className={classes.fileUploadContent}>
                        <div className={classes.fileUploadDetails}>
                            <div className={`${classes.skeletonLoader} ${classes.skeletonFilename}`} />
                            <div className={`${classes.skeletonLoader} ${classes.skeletonDateAuthor}`} />
                        </div>
                        <div>
                            <div className={`${classes.skeletonLoader} ${classes.skeletonMenu}`} />
                        </div>
                    </div>
                </div>
            ) : UPLOAD_STATUS_MESSAGES[uploadPhase] ? (
                <div className={classes.statusCard} role="status" aria-live="polite">
                    <Icon
                        name={uploadPhase === 'checkFailed' ? 'warning' : 'info'}
                        color={uploadPhase === 'checkFailed' ? 'redOne' : 'blueOne'}
                        size={24}
                    />
                    <div className={classes.fileUploadDetails}>
                        <Typography style={{ fontWeight: 600, fontSize: 15 }}>
                            {UPLOAD_STATUS_MESSAGES[uploadPhase]?.title}
                        </Typography>
                        {UPLOAD_STATUS_MESSAGES[uploadPhase]?.detail && (
                            <Typography style={{ fontSize: 13, color: '#4C525D' }}>
                                {UPLOAD_STATUS_MESSAGES[uploadPhase]?.detail}
                            </Typography>
                        )}
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            {lastCheckedAt && (
                                <Typography style={{ fontSize: 13, color: '#4C525D' }}>
                                    {`Last checked at ${lastCheckedAt.toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        // A check runs every 30 seconds, so without seconds the time
                                        // would only change on every other one
                                        second: '2-digit',
                                        hour12: true,
                                    })}`}
                                </Typography>
                            )}
                            {onCheckAgain && (
                                <Button variant="link" onPress={onCheckAgain}>
                                    Check again
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            ) : uploadPhase !== 'failed' &&
              publicationInfo &&
              publicationInfo.contentFile &&
              publicationInfo.contentFile?.fileName?.length > 0 ? (
                <>
                    <div
                        className={classes.fileUploadRow}
                        aria-disabled={disabled}
                        style={{ border: errorMessage ? '2px solid #de2622' : '1px solid #999DA3' }}
                    >
                        <div className={classes.fileUploadThumbnail}>
                            <img
                                className={classes.image}
                                src={thumbnail}
                                height={80}
                                alt={`Image for ${publicationInfo.contentFile.fileName}`}
                            />
                        </div>
                        <div className={classes.fileUploadContent}>
                            <div className={classes.fileUploadDetails}>
                                {fileAction ? (
                                    <Button
                                        variant="link"
                                        alignTitle="left"
                                        onPress={fileAction.onOpen}
                                        title={publicationInfo.contentFile.fileName}
                                        accessibilityLabel={`${fileAction.label} ${publicationInfo.contentFile.fileName}`}
                                    />
                                ) : (
                                    <Typography
                                        style={{
                                            fontWeight: 600,
                                            fontSize: 15,
                                        }}
                                    >
                                        {publicationInfo.contentFile.fileName}
                                    </Typography>
                                )}
                                <Typography style={{ fontSize: 13, color: '#4C525D' }}>
                                    {new Date(publicationInfo.contentFile.createdDate).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: true,
                                    })}{' '}
                                    | {publicationInfo.contentFile.createdBy}
                                </Typography>
                            </div>
                            <div>
                                <SelectPopover
                                    key="status-menu"
                                    isOpen={showStatusModal}
                                    options={actionMenu.map((menu) => (
                                        <Button
                                            key={menu.value}
                                            variant="ghost-gray"
                                            onPress={menu.action}
                                            alignTitle="left"
                                            title={menu.label}
                                        />
                                    ))}
                                    setShow={setShowStatusModal}
                                >
                                    <div>
                                        <Button variant="link" onPress={() => setShowStatusModal((prev) => !prev)}>
                                            <Icon
                                                size={30}
                                                name="more-dots-vertical"
                                                color="blueOne"
                                                raisedContainerStyle={{ width: 22, height: 22 }}
                                            />
                                        </Button>
                                    </div>
                                </SelectPopover>
                            </div>
                        </div>
                    </div>
                    {errorMessage && (
                        <Typography variant="error" style={{ marginTop: 8, color: '#de2622' }}>
                            {errorMessage}
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    <div
                        {...getRootProps()}
                        className={`${classes.uploadBox} p-4 rounded${disabled ? ' disabled' : ''}`}
                        style={{
                            border:
                                errorMessage || fileTypeError || uploadPhase === 'failed'
                                    ? '2px solid #de2622'
                                    : '1px solid #999DA3',
                        }}
                        tabIndex={disabled ? -1 : 0}
                        aria-disabled={disabled}
                    >
                        <input {...getInputProps({ disabled })} />
                        <div className="d-flex flex-column align-items-center justify-content-center text-center mt-2">
                            <div>
                                <Icon name={iconName} color="blueOneActive" />
                            </div>
                            {isDragActive ? (
                                <div>
                                    <Typography></Typography>
                                </div>
                            ) : (
                                <div className="d-flex flex-column align-items-center">
                                    <Typography
                                        style={{
                                            fontFamily: 'Roboto',
                                            fontWeight: 400,
                                            fontSize: 15,
                                            lineHeight: 24,
                                            letterSpacing: 0,
                                        }}
                                    >
                                        {placeholder1}
                                    </Typography>
                                    {placeholder2 && (
                                        <Typography
                                            style={{
                                                fontFamily: 'Roboto',
                                                fontWeight: 400,
                                                fontSize: 13,
                                                lineHeight: 16,
                                                letterSpacing: 0,
                                                color: '#4C525D',
                                            }}
                                        >
                                            {placeholder2}
                                        </Typography>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {uploadPhase === 'failed' && !fileTypeError ? (
                        <Typography variant="error" style={{ marginTop: 8, color: '#de2622' }}>
                            The previous upload failed. Please upload the file again.
                        </Typography>
                    ) : (
                        (errorMessage || fileTypeError) && (
                            <Typography variant="error" style={{ marginTop: 8, color: '#de2622' }}>
                                {fileTypeError || errorMessage}
                            </Typography>
                        )
                    )}
                </>
            )}
        </>
    );
};
